import debounce from 'lodash/debounce'
import { bridge } from '~/services/bridge'
import { getPluginAccessToken } from '~/services/bridge/pluginAuth'
import { debugConnection } from './connectionDebug'
import { connectionManager } from './connectionManager'
import {
  ApiForbiddenErrorPayload,
  AppEvents,
  extractApiErrorMessage,
  globalEventEmitter
} from './shared'
import { BetterToast } from './toast'
import { isProtectedAPIRoutePath } from './utils'

// ============================================================================
// Types
// ============================================================================

interface FetchWrapperOptions extends RequestInit {
  timeoutMs?: number
  isStreaming?: boolean
  suppressErrorToast?: boolean
}

interface FetchWrapper {
  (url: string, options?: FetchWrapperOptions): Promise<Response>
  get: <T = unknown>(url: string, options?: FetchWrapperOptions) => Promise<T>
  post: <T = unknown>(url: string, body?: unknown, options?: FetchWrapperOptions) => Promise<T>
  put: <T = unknown>(url: string, body?: unknown, options?: FetchWrapperOptions) => Promise<T>
  patch: <T = unknown>(url: string, body?: unknown, options?: FetchWrapperOptions) => Promise<T>
  delete: <T = unknown>(url: string, options?: FetchWrapperOptions) => Promise<T>
}

// ============================================================================
// Auth Helpers
// ============================================================================

const redirectToSignIn = debounce(() => {
  BetterToast.error('Authentication required. Please sign in.')
  if (bridge.isInPlugin()) {
    globalEventEmitter.emit(AppEvents.ApiForbiddenError, {
      errorType: 'auth_required',
      message: 'Authentication required',
      url: ''
    })
  } else {
    window.location.href = '/signin'
  }
}, 500)

// ============================================================================
// Error Handlers
// ============================================================================

/**
 * Emit event for 403 Forbidden errors.
 * ModalContext listens to this and handles specific error types (upgrade, quota, read-only).
 */
function handle403Error(response: Response, url: string): void {
  response
    .clone()
    .json()
    .then(({ detail }) => {
      const { error: errorType, message } = detail || {}
      const payload: ApiForbiddenErrorPayload = { errorType, message, url }
      globalEventEmitter.emit(AppEvents.ApiForbiddenError, payload)
    })
    .catch(() => {
      // Ignore JSON parsing issues
    })
}

/**
 * Show toast for generic errors (non-403).
 * 403 errors are handled via event system.
 */
function handleGenericError(response: Response, suppressErrorToast: boolean): void {
  if (response.ok || suppressErrorToast || response.status === 403) {
    return
  }

  response
    .clone()
    .json()
    .then(errorData => {
      const errorMessage = extractApiErrorMessage(errorData, '')
      if (errorMessage.trim().length > 0) {
        BetterToast.error(errorMessage)
      }
    })
    .catch(() => {
      // Ignore if response is not JSON
    })
}

/**
 * Handle 401 Unauthorized - redirect to sign in.
 */
function handle401Error(response: Response, url: string): boolean {
  if (!isProtectedAPIRoutePath(url)) {
    return false
  }

  if (response.status === 401) {
    redirectToSignIn()
    return true
  }

  return false
}

// ============================================================================
// Response Parser
// ============================================================================

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage = 'An error occurred'

    try {
      const errorJson = JSON.parse(errorText)
      errorMessage = extractApiErrorMessage(errorJson, errorText || errorMessage)
    } catch {
      errorMessage = errorText || errorMessage
    }

    throw new Error(errorMessage)
  }

  const text = await response.text()
  if (!text) {
    return {} as T
  }

  const contentType = response.headers.get('content-type')
  const isJsonResponse = contentType && contentType.includes('application/json')

  try {
    return JSON.parse(text) as T
  } catch (error) {
    if (text.trim().startsWith('<')) {
      throw new Error(
        'API server returned HTML instead of JSON. This usually means the API endpoint does not exist or the server is not running properly.'
      )
    } else if (!isJsonResponse) {
      throw new Error(
        `Expected JSON response but received content-type: ${contentType || 'unknown'}`
      )
    } else {
      throw new Error(
        `Invalid JSON response: ${error instanceof Error ? error.message : 'Parse error'}`
      )
    }
  }
}

// ============================================================================
// Core Fetch Logic
// ============================================================================

async function executeWithTimeout(
  url: string,
  fetchOptions: RequestInit,
  timeoutMs: number
): Promise<Response> {
  const timeoutController = new AbortController()
  const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs)

  // Combine caller's signal with timeout signal so both can abort the request
  const externalSignal = fetchOptions.signal
  if (externalSignal) {
    /* c8 ignore start -- tested in fetchWrapper.test.ts; coverage flaky in jsdom */
    if (externalSignal.aborted) {
      clearTimeout(timeoutId)
      throw new DOMException('The operation was aborted.', 'AbortError')
    }
    /* c8 ignore stop */
    externalSignal.addEventListener('abort', () => timeoutController.abort(), { once: true })
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: timeoutController.signal
    })
    clearTimeout(timeoutId)
    return response
  } catch (error: unknown) {
    clearTimeout(timeoutId)

    // If the external signal was the one that aborted, re-throw as AbortError
    /* c8 ignore start -- tested in fetchWrapper.test.ts; coverage flaky in jsdom */
    if (externalSignal?.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError')
    }
    /* c8 ignore stop */

    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeoutMs / 1000} seconds: ${url}`)
    }
    throw error
  }
}

async function baseFetchWrapper(url: string, options: FetchWrapperOptions = {}): Promise<Response> {
  const { suppressErrorToast = false, timeoutMs, isStreaming, ...fetchOptions } = options

  // Plugin mode: resolve relative URLs to absolute (webview origin differs from API)
  if (bridge.isInPlugin() && url.startsWith('/')) {
    url = `${bridge.getBaseUrl()}${url}`
  }

  if (fetchOptions.credentials === undefined) {
    fetchOptions.credentials = 'include'
  }

  // Plugin mode: tell the API this is a plugin request so it sets SameSite=None on cookies
  if (bridge.isInPlugin()) {
    const headers = new Headers(fetchOptions.headers as HeadersInit)
    headers.set('X-Curietech-Plugin', 'true')
    if (!headers.has('Authorization')) {
      const token = getPluginAccessToken()
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
    }
    fetchOptions.headers = headers
  }

  // Determine request characteristics
  const method = fetchOptions.method || 'GET'
  const isStreamingRequest = isStreaming ?? (url.includes('/chat') && method === 'POST')
  const resolvedTimeout = timeoutMs ?? (isStreamingRequest ? 120000 : 90000)
  const priority = connectionManager.getRequestPriority(url)

  // Log request start
  const startTime = Date.now()
  debugConnection.logRequest(url, method, priority)

  // Execute request
  let response: Response
  try {
    if (isStreamingRequest) {
      debugConnection.log(`Streaming request bypassing queue: ${method} ${url}`)
      response = await executeWithTimeout(url, fetchOptions, resolvedTimeout)
    } else {
      response = await connectionManager.execute(
        () => executeWithTimeout(url, fetchOptions, resolvedTimeout),
        priority
      )
    }
    debugConnection.logRequestComplete(url, method, Date.now() - startTime, response.ok)
  } catch (error) {
    debugConnection.logRequestComplete(url, method, Date.now() - startTime, false)
    throw error
  }

  // Handle response errors
  if (response.status === 401) {
    handle401Error(response, url)
  } else if (response.status === 403) {
    handle403Error(response, url)
  } else if (!response.ok) {
    handleGenericError(response, suppressErrorToast)
  }

  return response
}

// ============================================================================
// Public API
// ============================================================================

export const fetchWrapper: FetchWrapper = Object.assign(baseFetchWrapper, {
  get: async <T>(url: string, options: FetchWrapperOptions = {}): Promise<T> => {
    const response = await baseFetchWrapper(url, {
      ...options,
      method: 'GET',
      headers: { 'Content-Type': 'application/json', ...options.headers }
    })
    return handleResponse<T>(response)
  },

  post: async <T>(url: string, body?: unknown, options: FetchWrapperOptions = {}): Promise<T> => {
    const response = await baseFetchWrapper(url, {
      ...options,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...options.headers },
      body: body ? JSON.stringify(body) : undefined
    })
    return handleResponse<T>(response)
  },

  put: async <T>(url: string, body?: unknown, options: FetchWrapperOptions = {}): Promise<T> => {
    const response = await baseFetchWrapper(url, {
      ...options,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...options.headers },
      body: body ? JSON.stringify(body) : undefined
    })
    return handleResponse<T>(response)
  },

  patch: async <T>(url: string, body?: unknown, options: FetchWrapperOptions = {}): Promise<T> => {
    const response = await baseFetchWrapper(url, {
      ...options,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...options.headers },
      body: body ? JSON.stringify(body) : undefined
    })
    return handleResponse<T>(response)
  },

  delete: async <T>(url: string, options: FetchWrapperOptions = {}): Promise<T> => {
    const response = await baseFetchWrapper(url, {
      ...options,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...options.headers }
    })
    return handleResponse<T>(response)
  }
})
